import React, { useMemo } from 'react';
import { RiPieChart2Fill } from "react-icons/ri";
import { IoIosPricetags } from "react-icons/io";

const PerformanceStats = ({ overall = {}, weakAreas }) => {
    console.log('weakAreas', weakAreas);
    /**
     ✅ SAFE FALLBACKS
     Never trust API blindly.
     */
    const {
        percentage,
        correctAnswers = 0,
        wrongAnswers = 0,
        skipped = 0,
        timeTakenSeconds = 0,
        totalQuestions = 0
    } = overall ?? {};


    /**
     ✅ AUTO CALCULATE percentage if backend forgets
     */
    const safePercentage = useMemo(() => {

        if (typeof percentage === "number") {
            return Math.min(Math.max(percentage, 0), 100);
        }

        if (!totalQuestions) return 0;

        return Math.round((correctAnswers / totalQuestions) * 100);

    }, [percentage, correctAnswers, totalQuestions]);


    /**
     ✅ Better Time Format (01m 05s)
     */
    const formattedTime = useMemo(() => {

        const minutes = String(Math.floor(timeTakenSeconds / 60)).padStart(2, '0');
        const seconds = String(timeTakenSeconds % 60).padStart(2, '0');

        return `${minutes}m ${seconds}s`;

    }, [timeTakenSeconds]);


    /**
     ✅ Rotation SAFE
     */
    const rotation = useMemo(() => {
        return `rotate(${(safePercentage / 100) * 360}deg)`;
    }, [safePercentage]);


    /**
     ✅ WeakAreas SAFE
     */
    const safeWeakAreas = Array.isArray(weakAreas) ? weakAreas : [];


    return (
        <div className="w-full lg:w-[300px] flex flex-col gap-6 shrink-0">

            {/* PERFORMANCE SUMMARY */}
            <div className="bg-white border border-[#E0E0E0] rounded-[10px] p-6 shadow-sm">

                <h3 className="flex items-center gap-2 font-[500] text-[#000000] mb-6 text-[14px]">
                    <RiPieChart2Fill className="text-[20px]" />
                    Performance Summary
                </h3>

                <div className="flex flex-col items-center">

                    {/* Circle */}
                    <div className="w-32 h-32 rounded-full border-[10px] border-gray-100 relative flex items-center justify-center mb-8">

                        <div
                            style={{ transform: rotation }}
                            className="absolute inset-0 rounded-full border-[10px] border-green-500 border-t-transparent transition-transform duration-700 ease-out"
                        />

                        <div className="text-center">
                            <p className="text-[24px] font-bold leading-none">
                                {safePercentage}%
                            </p>

                            <p className="text-[10px] text-[#2F2F2F] uppercase mt-2">
                                Correct
                            </p>
                        </div>
                    </div>


                    {/* Stats */}
                    <div className="w-full space-y-3">

                        <div className="flex justify-between font-[500] border-b border-[#D5D5D5] pb-2 text-[12px]">
                            <span>Correct Answers</span>
                            <span className="text-[#21C11E]">
                                {correctAnswers}
                            </span>
                        </div>

                        <div className="flex justify-between font-[500] border-b border-[#D5D5D5] pb-2 text-[12px]">
                            <span>Incorrect Answers</span>
                            <span className="text-[#E63946]">
                                {wrongAnswers}
                            </span>
                        </div>

                        <div className="flex justify-between font-[500] border-b border-[#D5D5D5] pb-2 text-[12px]">
                            <span>Skipped Questions</span>
                            <span className="text-[#9A9A9A]">
                                {skipped}
                            </span>
                        </div>

                        <div className="flex justify-between text-[12px] font-[600] pt-2">
                            <span>Total Time</span>
                            <span>{formattedTime}</span>
                        </div>

                    </div>
                </div>
            </div>


            {/* WEAK AREAS */}
            {weakAreas.length > 0 && (
                <div className="bg-white border border-[#E0E0E0] rounded-[10px] p-6 shadow-sm">

                    <h3 className="flex items-center gap-2 justify-center font-[500] text-[#000000] mb-6 text-[14px]">
                        <IoIosPricetags className="text-[24px]" />
                        Weak Areas
                    </h3>

                    <div className="space-y-4">
                        {weakAreas.map((item, i) => (
                            <div
                                key={item?.l ?? i}
                                className="flex justify-between text-[12px] border-b border-[#D5D5D5] pb-2"
                            >
                                <span className="font-[500]">
                                    {item?.name ?? "Unknown"}
                                </span>

                                <span className="text-[#E63946] font-[500]">
                                    {item?.percentage + "%" ?? "-"}
                                </span>
                            </div>
                        ))}
                    </div>

                </div>
            )}

        </div>
    );
};

export default PerformanceStats;
